import { useLanguage } from '../hooks/useLanguage';
import { Link } from 'react-router-dom';
import { ChefHat, ArrowRight, ArrowLeft, FileText } from 'lucide-react';

export default function TermsPage() {
  const { isAr, dir, lang, toggleLanguage } = useLanguage();
  const l = (ar, en) => isAr ? ar : en;

  const sections = [
    {
      title: l('1. مقدمة', '1. Introduction'),
      content: l(
        'مرحباً بكم في منصة ذوق AI. باستخدامكم للمنصة، توافقون على الالتزام بشروط الاستخدام هذه. يرجى قراءتها بعناية قبل استخدام أي من خدماتنا. إذا كنتم لا توافقون على هذه الشروط، يرجى عدم استخدام المنصة.',
        'Welcome to Zouq AI. By using the platform, you agree to be bound by these Terms of Service. Please read them carefully before using any of our services. If you do not agree to these terms, please do not use the platform.'
      ),
    },
    {
      title: l('2. وصف الخدمة', '2. Service Description'),
      content: l(
        'ذوق AI منصة إدارة أعمال غذائية شاملة تقدم الخدمات التالية:\n\n• نظام نقاط البيع (POS) لإدارة المبيعات والطلبات\n• شاشة مطبخ ذكية لإدارة التحضير\n• وكيل طلبات ذكي (ZouqBot) مدعوم بالذكاء الاصطناعي\n• نظام محاسبة وإدارة مالية\n• فوترة إلكترونية متوافقة مع زاتكا (ZATCA)\n• إدارة أسطول التوصيل\n• أداة بناء المواقع التحادثية\n• إدارة الفريق والصلاحيات\n• إدارة الفروع والقوائم والمخزون',
        'Zouq AI is a comprehensive food business management platform offering the following services:\n\n• Point of Sale (POS) system for sales and order management\n• Smart kitchen display for preparation management\n• AI-powered smart ordering agent (ZouqBot)\n• Accounting and financial management system\n• ZATCA-compliant electronic invoicing\n• Delivery fleet management\n• Conversational website builder\n• Team management and permissions\n• Branch, menu, and inventory management'
      ),
    },
    {
      title: l('3. إنشاء الحساب', '3. Account Creation'),
      content: l(
        '• يجب أن يكون عمركم 18 عاماً أو أكثر لإنشاء حساب\n• أنتم مسؤولون عن الحفاظ على سرية بيانات الدخول\n• يجب تقديم معلومات دقيقة وحديثة عند التسجيل\n• أنتم مسؤولون عن جميع الأنشطة التي تتم تحت حسابكم\n• يجب إبلاغنا فوراً عن أي استخدام غير مصرح به لحسابكم',
        '• You must be 18 years or older to create an account\n• You are responsible for maintaining the confidentiality of your login credentials\n• You must provide accurate and current information during registration\n• You are responsible for all activities under your account\n• You must notify us immediately of any unauthorized use of your account'
      ),
    },
    {
      title: l('4. الاستخدام المقبول', '4. Acceptable Use'),
      content: l(
        'عند استخدام المنصة، تتعهدون بعدم:\n\n• استخدام المنصة لأي أغراض غير قانونية\n• انتهاك حقوق الملكية الفكرية أو حقوق الغير\n• محاولة الوصول غير المصرح به لأنظمتنا\n• إرسال برمجيات ضارة أو فيروسات\n• استخدام المنصة بطريقة تضر بتجربة المستخدمين الآخرين\n• جمع بيانات المستخدمين الآخرين بدون إذن\n• استخدام ZouqBot لأغراض مضللة أو احتيالية',
        'When using the platform, you agree not to:\n\n• Use the platform for any unlawful purposes\n• Violate intellectual property rights or third-party rights\n• Attempt unauthorized access to our systems\n• Send malicious software or viruses\n• Use the platform in a way that harms other users\' experience\n• Collect other users\' data without permission\n• Use ZouqBot for misleading or fraudulent purposes'
      ),
    },
    {
      title: l('5. خطط الاشتراك والدفع', '5. Subscription Plans & Payment'),
      content: l(
        '• نقدم خطط اشتراك متنوعة (تجربة مجانية، أساسي، احترافي، مؤسسات)\n• التجربة المجانية متاحة لمدة 14 يوماً بدون بطاقة ائتمان\n• الأسعار بالريال السعودي وتشمل ضريبة القيمة المضافة 15%\n• يتم الفوترة شهرياً ما لم يُتفق على خلاف ذلك\n• يمكنكم ترقية أو تخفيض خطتكم في أي وقت\n• نحتفظ بحق تعديل الأسعار مع إشعار مسبق بـ 30 يوماً',
        '• We offer various subscription plans (Free Trial, Basic, Pro, Enterprise)\n• Free trial available for 14 days without credit card\n• Prices are in Saudi Riyals and include 15% VAT\n• Billing is monthly unless otherwise agreed\n• You can upgrade or downgrade your plan at any time\n• We reserve the right to modify prices with 30 days advance notice'
      ),
    },
    {
      title: l('6. الملكية الفكرية', '6. Intellectual Property'),
      content: l(
        '• جميع حقوق الملكية الفكرية للمنصة محفوظة لشركة ذوق AI\n• يشمل ذلك الكود البرمجي، التصميم، الشعارات، والمحتوى\n• بياناتكم التجارية (القوائم، الطلبات، التقارير) تبقى ملكاً لكم\n• تمنحوننا ترخيصاً محدوداً لاستخدام بياناتكم لتقديم الخدمة\n• المواقع المُنشأة عبر أداة البناء تبقى ملكاً لكم',
        '• All intellectual property rights of the platform are reserved to Zouq AI\n• This includes source code, design, logos, and content\n• Your business data (menus, orders, reports) remains your property\n• You grant us a limited license to use your data to provide the service\n• Websites created via the builder tool remain your property'
      ),
    },
    {
      title: l('7. إخلاء المسؤولية', '7. Disclaimer'),
      content: l(
        '• المنصة تُقدم "كما هي" بدون ضمانات صريحة أو ضمنية\n• لا نضمن أن الخدمة ستكون خالية من الأخطاء أو الانقطاعات\n• ZouqBot يعمل بالذكاء الاصطناعي وقد يقدم ردوداً غير دقيقة أحياناً\n• أنتم مسؤولون عن مراجعة الفواتير الإلكترونية قبل إصدارها\n• لا نتحمل مسؤولية أي خسائر ناتجة عن استخدام المنصة',
        '• The platform is provided "as is" without express or implied warranties\n• We do not guarantee the service will be error-free or uninterrupted\n• ZouqBot operates with AI and may occasionally provide inaccurate responses\n• You are responsible for reviewing electronic invoices before issuance\n• We are not liable for any losses resulting from platform use'
      ),
    },
    {
      title: l('8. الإنهاء', '8. Termination'),
      content: l(
        '• يمكنكم إنهاء حسابكم في أي وقت من خلال إعدادات الحساب\n• نحتفظ بحق تعليق أو إنهاء حسابكم في حالة انتهاك هذه الشروط\n• عند الإنهاء، ستكون لديكم 30 يوماً لتصدير بياناتكم\n• البيانات المطلوبة قانونياً (مثل الفواتير) ستُحتفظ وفقاً لسياسة الاحتفاظ',
        '• You can terminate your account at any time through account settings\n• We reserve the right to suspend or terminate your account for violations\n• Upon termination, you will have 30 days to export your data\n• Legally required data (e.g., invoices) will be retained per retention policy'
      ),
    },
    {
      title: l('9. القانون المطبق', '9. Governing Law'),
      content: l(
        'تخضع هذه الشروط لأنظمة المملكة العربية السعودية. أي نزاعات تُحل وفقاً للأنظمة السعودية المعمول بها، وتختص المحاكم السعودية بالنظر فيها.',
        'These terms are governed by the laws of the Kingdom of Saudi Arabia. Any disputes shall be resolved in accordance with applicable Saudi regulations, and Saudi courts shall have jurisdiction.'
      ),
    },
    {
      title: l('10. التواصل', '10. Contact'),
      content: l(
        'لأي استفسارات حول شروط الاستخدام:\n\n• البريد الإلكتروني: support@zouqai.com\n• العنوان: المملكة العربية السعودية\n\nآخر تحديث: فبراير 2026',
        'For any inquiries about the Terms of Service:\n\n• Email: support@zouqai.com\n• Address: Kingdom of Saudi Arabia\n\nLast updated: February 2026'
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background" dir={dir} style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <ChefHat className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>Zouq AI</span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={toggleLanguage} className="px-2.5 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground transition-colors" style={{ background: 'hsl(var(--muted))' }}>
              {lang === 'ar' ? 'EN' : 'عربي'}
            </button>
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors" data-testid="terms-back">
              {isAr ? <ArrowRight className="w-3 h-3" /> : <ArrowLeft className="w-3 h-3" />}
              {l('الرئيسية', 'Home')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }} data-testid="terms-title">
              {l('شروط الاستخدام', 'Terms of Service')}
            </h1>
            <p className="text-xs text-muted-foreground">{l('آخر تحديث: فبراير 2026', 'Last updated: February 2026')}</p>
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((sec, i) => (
            <section key={i} data-testid={`terms-section-${i}`}>
              <h2 className="text-base font-bold text-foreground mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>{sec.title}</h2>
              <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{sec.content}</div>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-border text-center">
          <Link to="/privacy" className="text-xs text-amber-500 hover:underline" data-testid="terms-to-privacy">
            {l('اقرأ سياسة الخصوصية', 'Read Privacy Policy')}
          </Link>
        </div>
      </div>
    </div>
  );
}
